"use client"

import React, { useEffect } from 'react'
import ActivityChart from './activity_chart'
import LeftRight from '../left-right'
import { Button } from '@/components/ui/button'
import { PlusIcon, Grid2x2 } from 'lucide-react'
import { DateRangePicker } from '@/components/ui/date-range-picker'
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select'
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs'
import {Route as dashboardRoute} from '@/routes/(inventory)/branch/_layout/dashboard/index'
import dayjs from 'dayjs'
import { useMutation } from '@connectrpc/connect-query'
import { getStockPoints } from '@/gen/pharmacy-PharmacyService_connectquery'
import { toast } from '@/components/ui/use-toast'
import { GetStockPointsRequest } from '@/gen/branch/get_stock_points_pb'
import AddStockPointSheet from './add-stock-point-sheet'
import type { DateRange } from "react-day-picker"

const DepartmentOverview = () => {
  const [selectedDepartment, setSelectedDepartment] = React.useState('all')
  const [selectedTab, setSelectedTab] = React.useState<'month' | 'week' | 'custom'>('month')
  const {stockPoinstInfo} = dashboardRoute.useLoaderData();
  const [dateRange, setDateRange] = React.useState<DateRange | undefined>({
    from: dayjs().startOf('month').toDate(),
    to: dayjs().endOf('month').toDate(),
  })
  const departmentOptions = [
    { value: 'all', label: 'All Stock Points' },
  ]

  const [data, setData] = React.useState(stockPoinstInfo);

  // Dynamically create department options based on data
  data.stockPoints.forEach((stockPoint) => {
    departmentOptions.push({
      value: stockPoint.id,
      label: stockPoint.name,
    })
  })

  const departmentColors = [
    '#FF6B6B', // red
    '#6BCB77', // green
    '#4D96FF', // blue
    '#FFD93D', // yellow
    '#A66CFF', // purple
  ]


  useEffect(() => {
    if (selectedTab === 'month') {
      setDateRange({
        from: dayjs().startOf('month').toDate(),
        to: dayjs().endOf('month').toDate(),
      })
    } else if (selectedTab === 'week') {
      setDateRange({
        from: dayjs().startOf('week').toDate(),
        to: dayjs().endOf('week').toDate(),
      })
    }
  }, [selectedTab])

  const stockPointDataMutation = useMutation(getStockPoints, {
    onSuccess: (data) => {
      setData(data)
    },
    onError: (error) => {
      toast({
        title: 'Error fetching stock points',
        description: 'There was an error fetching stock points. Please try again later.',
        variant: 'destructive',
        duration: 5000,
      })
      console.error('Error fetching stock points:', error);
    },
  })

  useEffect(() => {
    if (!dateRange?.from || !dateRange?.to) return; // Only fetch when both dates are set
    const data = new GetStockPointsRequest()
    data.startDate = dayjs(dateRange.from).unix()
    data.endDate = dayjs(dateRange.to).unix()
    stockPointDataMutation.mutate(data)
  }, [dateRange])

  type ChartDataItem = { [key: string]: string | number; month: string };

  // Collect all unique x-axis points (dates) and all department ids
  const allMonthsSet = new Set<string>();
  const allDepartmentIds = data.stockPoints.map(sp => sp.id);
  data.stockPointActivity.forEach(activity => {
    const month = dayjs(activity.timestamp?.toDate()).format('DD/MM');
    allMonthsSet.add(month);
  });
  const allMonths = Array.from(allMonthsSet).sort((a, b) => {
    // Sort by date
    const [da, ma] = a.split('/').map(Number);
    const [db, mb] = b.split('/').map(Number);
    return ma !== mb ? ma - mb : da - db;
  });

  // Build a map: month -> { departmentId: value }
  const monthDeptMap: Record<string, Record<string, number>> = {};
  data.stockPointActivity.forEach(activity => {
    const month = dayjs(activity.timestamp?.toDate()).format('DD/MM');
    if (!monthDeptMap[month]) monthDeptMap[month] = {};
    monthDeptMap[month][activity.id] = (monthDeptMap[month][activity.id] || 0) + activity.activityValue;
  });

  // Build chartData: for each month, ensure all departments are present
  const chartData: ChartDataItem[] = allMonths.map(month => {
    const item: ChartDataItem = { month };
    allDepartmentIds.forEach(deptId => {
      item[deptId] = monthDeptMap[month]?.[deptId] ?? 0;
    });
    return item;
  });

  return (
    <div className='mt-4'>
      <LeftRight 
        left={`${data.stockPoints.length} stock points added`}
        right={<AddStockPointSheet />}
      />
      <LeftRight 
        left={`${Math.max(...data.stockPoints.map(sp => sp.totalUsersWithAccess))} users across all stock points`}
        right={<Button
          size={"xs"}
          className='bg-white border text-[#6F7B7C] opacity-25 text-sm font-semibold cursor-not-allowed '
        >
        <PlusIcon
          className='text-black w-4 h-4 disabled cursor-not-allowed'
        />
          Add people
        </Button>
        } 
        className="mt-2"
      />
      <div
        className='mt-8 flex items-center justify-between'
      >
        <div className='flex gap-2 items-center'>
          <DateRangePicker 
            date={dateRange}
            onDateChange={setDateRange}
            disabled={selectedTab !== 'custom'}
          />
          <Select value={selectedDepartment} onValueChange={setSelectedDepartment}>
            <SelectTrigger className="">
              <SelectValue placeholder="Select department...">
                {(() => {
                  const selected = departmentOptions.find(opt => opt.value === selectedDepartment)
                  const idx = departmentOptions.findIndex(opt => opt.value === selectedDepartment)
                  const color = idx > 0 ? departmentColors[(idx - 1) % departmentColors.length] : undefined
                  return selected ? (
                    <span className="flex items-center gap-2">
                      {selected.value !== 'all' && <Grid2x2 color={color} className="w-4 h-4" />}
                      {selected.label}
                    </span>
                  ) : null
                })()}
              </SelectValue>
            </SelectTrigger>
            <SelectContent>
              {departmentOptions.map((option, idx) => (
                <SelectItem key={option.value} value={option.value}>
                  <span className="flex items-center gap-2">
                    {option.value !== 'all' && <Grid2x2 color={departmentColors[(idx - 1) % departmentColors.length]} className="w-4 h-4" />}
                    {option.label}
                  </span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <Tabs value={selectedTab} onValueChange={(value) => setSelectedTab(value as 'month' | 'week' | 'custom')}>
          <TabsList>
            <TabsTrigger value="custom">Custom</TabsTrigger>
            <TabsTrigger value="week">Week</TabsTrigger>
            <TabsTrigger value="month">Month</TabsTrigger>
          </TabsList>
        </Tabs>
      </div>
      <ActivityChart
        departmentOptions={departmentOptions.filter(opt => opt.value !== 'all')}
        departmentColors={departmentColors}
        chartData={chartData}
        visibleDepartments={selectedDepartment === 'all' ? departmentOptions.filter(opt => opt.value !== 'all').map(opt => opt.value) : [selectedDepartment]}
      />
    </div>
  )
}

export default DepartmentOverview